import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Pencil, Trash2, Eye, EyeOff, Loader2, Package, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import api, { readError } from "@/lib/api";

export default function AdminProducts() {
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");

  const load = async () => {
    setError("");
    try {
      const { data } = await api.get("/admin/product-types");
      setItems(data);
    } catch (err) {
      setError(readError(err, "Could not load products."));
    }
  };

  useEffect(() => { load(); }, []);

  const del = async (id) => {
    try {
      await api.delete(`/admin/product-types/${id}`);
      toast.success("Product hidden (soft delete).");
      await load();
    } catch (err) {
      toast.error(readError(err, "Could not delete product."));
    }
  };

  return (
    <div className="space-y-8 animate-fade-up">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-primary">CMS</div>
          <h1 className="mt-1 font-serif text-4xl text-foreground">Products</h1>
          <p className="mt-2 text-sm text-muted-foreground max-w-2xl">
            Tamburitza models, prices, descriptions in HR/EN/DE/FR.
          </p>
        </div>
        <Link to="/admin/products/new">
          <Button data-testid="new-product-btn" className="shadow-interactive">
            <Plus className="h-4 w-4" strokeWidth={1.5} /> New product
          </Button>
        </Link>
      </div>

      {error && (
        <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 mt-0.5" strokeWidth={1.5} /> {error}
        </div>
      )}

      {items === null ? (
        <div className="p-10 flex items-center justify-center text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading products…
        </div>
      ) : items.length === 0 ? (
        <Card className="bg-card border-border/60 shadow-ambient">
          <CardContent className="p-10 text-center">
            <Package className="h-10 w-10 mx-auto text-muted-foreground/40" strokeWidth={1.5} />
            <p className="mt-3 text-sm text-muted-foreground">No products yet.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="products-grid">
          {items.map((p) => (
            <Card key={p.id} data-testid={`product-card-${p.id}`} className="bg-card border-border/60 shadow-ambient overflow-hidden">
              <div className="aspect-[5/3] bg-muted overflow-hidden">
                {p.hero_image_url && (
                  <img src={p.hero_image_url} alt="" className={`w-full h-full object-cover ${p.visible ? "" : "opacity-40 grayscale"}`} loading="lazy" />
                )}
              </div>
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-serif text-xl text-foreground truncate">{p.name?.hr || p.id}</div>
                    <div className="text-xs text-muted-foreground truncate">{p.slug_path}</div>
                  </div>
                  <Badge variant={p.visible ? "default" : "secondary"} className={p.visible ? "bg-primary text-primary-foreground" : ""}>
                    {p.visible ? <Eye className="h-3 w-3 mr-1" strokeWidth={1.5} /> : <EyeOff className="h-3 w-3 mr-1" strokeWidth={1.5} />}
                    {p.visible ? "Live" : "Hidden"}
                  </Badge>
                </div>
                <div className="mt-3 text-sm text-foreground/70 line-clamp-2">{p.tagline?.hr}</div>
                <div className="mt-3 flex items-baseline gap-2">
                  <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">From</span>
                  <span className="text-foreground font-medium">€{p.base_price_eur}</span>
                </div>

                <div className="mt-4 flex items-center gap-2">
                  <Link to={`/admin/products/${p.id}`} className="flex-1">
                    <Button variant="outline" size="sm" className="w-full" data-testid={`edit-product-${p.id}`}>
                      <Pencil className="h-3.5 w-3.5" strokeWidth={1.5} /> Edit
                    </Button>
                  </Link>
                  {p.visible && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="outline" size="sm" data-testid={`delete-product-${p.id}`} className="text-destructive border-destructive/40 hover:bg-destructive/10 hover:text-destructive">
                          <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="bg-card border-border">
                        <AlertDialogHeader>
                          <AlertDialogTitle className="font-serif">Hide this product?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This will set <strong className="text-foreground">{p.name?.hr || p.id}</strong> as hidden from the public site (soft delete). You can re-enable it later.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => del(p.id)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                            Yes, hide
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
